import { Fragment, type JSX, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import type { LegalDocument } from '@kitup/brand';
import { useBrand } from '../brand/context.js';
import { useLegal } from '../brand/legal-context.js';

/**
 * Wrapper for the legal pages. Contains no texts of its own: the document list,
 * the requisites, and the documents themselves all come from the brand's legal
 * bundle. The "Public Offer" is an instrument of Russian law; the North
 * American brand has a different document in its place, not a translation.
 */
export function LegalIndexPage(): JSX.Element {
  const legal = useLegal();
  return (
    <LegalShell title={legal.indexTitle}>
      {legal.indexIntro()}
      <section>
        <h2>{legal.requisitesTitle}</h2>
        <dl className="mt-3 grid gap-2 sm:grid-cols-[180px_1fr]">
          {legal.requisites.map((requisite) => (
            <Fragment key={requisite.label}>
              <dt>{requisite.label}</dt>
              <dd>
                {requisite.href ? <a href={requisite.href}>{requisite.value}</a> : requisite.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      </section>
      <section>
        <h2>{legal.ui.documentsTitle}</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {legal.documents.map((document) => (
            <li key={document.id} className="rounded-xl border border-slate-200 p-4">
              <Link className="font-semibold underline" to={`/legal/${document.id}`}>
                {document.title}
              </Link>
              <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{document.summary}</p>
            </li>
          ))}
        </ul>
      </section>
      {legal.indexOutro ? (
        <section>
          <h2>{legal.indexOutroTitle}</h2>
          {legal.indexOutro()}
        </section>
      ) : null}
    </LegalShell>
  );
}

export function LegalDocumentPage(): JSX.Element {
  const legal = useLegal();
  const { document: documentId } = useParams();
  const document = legal.documents.find((candidate) => candidate.id === documentId);
  if (!document) return <Navigate to="/legal" replace />;
  return <LegalShell title={pageTitleOf(document)}>{document.render({ Link })}</LegalShell>;
}

function pageTitleOf(document: LegalDocument): string {
  return document.pageTitle ?? document.title;
}

function LegalShell({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): JSX.Element {
  const legal = useLegal();
  const { support } = useBrand();
  return (
    <main className="mx-auto min-h-screen max-w-5xl px-4 py-8 sm:py-12">
      <Link className="text-sm underline" to="/">
        {legal.ui.backHome}
      </Link>
      <article className="kitup-card mt-5 p-5 sm:p-8 [&_a]:underline [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-7 [&_ol]:mt-3 [&_p]:mt-3 [&_p]:leading-7 [&_section_li]:ml-5 [&_section_ol_li]:list-decimal [&_section_ul_li]:list-disc [&_section_ul]:mt-3 [&_strong]:font-semibold">
        <header>
          <p className="text-sm text-[var(--kitup-color-muted)]">
            {legal.ui.editionPrefix} {legal.documentVersion}
            {legal.documentVersionId
              ? ` · ${legal.ui.versionPrefix} ${legal.documentVersionId}`
              : ''}
          </p>
          <h1 className="mt-2 text-3xl font-semibold">{title}</h1>
        </header>
        {children}
      </article>
      <footer className="mt-6 flex flex-wrap gap-4 text-sm">
        <Link to="/legal">{legal.ui.allDocuments}</Link>
        <a href={`mailto:${support.email}`}>{support.email}</a>
      </footer>
    </main>
  );
}
